import React, { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { coverageApi, reportApi, researchApi } from '../services/api';
import { useReportViewTracking } from '../services/reportViews';
import Brand from './Brand';
import HeaderNav from './HeaderNav';
import ResearchDisclaimer from './ResearchDisclaimer';
import './CompanyReport.css';

// Also the display order of the product buttons.
const PRODUCT_LABELS = {
  firstlook: 'First Look',
  deepdive: 'Deep Dive',
  rabbithole: 'Rabbit Hole',
  course: 'Course',
  grammar: 'Grammar Course'
};
const PAUSED_REPORT_PRODUCTS = new Set(['rabbithole', 'course']);

const list = (value) => (Array.isArray(value) ? value : []);
const textValue = (value) => (value == null ? '' : String(value));
const safeHref = (url) => (/^https?:\/\//i.test(String(url || '')) ? url : null);
const hostOf = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
};
const niceDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const isAllCapsAlias = (value) => /[A-Z]/.test(value) && value === value.toUpperCase();

const HoverContext = createContext(null);
const SourcesContext = createContext({ byId: new Map(), sources: [] });

const makeHover = (hover) => {
  const aliases = [];
  list(hover).forEach((entry, entryIndex) => {
    [...new Set([entry.term, ...list(entry.aliases)].filter(Boolean))].forEach((alias, aliasIndex) => {
      const label = textValue(alias).trim();
      if (label) aliases.push({ label, entry, key: `${entryIndex}-${aliasIndex}-${label}`, exact: isAllCapsAlias(label) });
    });
  });
  if (!aliases.length) return null;
  aliases.sort((a, b) => b.label.length - a.label.length);
  return {
    aliases,
    regex: new RegExp(`\\b(?:${aliases.map((item) => escapeRegExp(item.label)).join('|')})\\b`, 'gi')
  };
};

const matchingAlias = (label, hover) => hover?.aliases.find((item) => (
  item.exact ? item.label === label : item.label.toLowerCase() === label.toLowerCase()
));

const JargonTerm = ({ label, entry }) => {
  const id = useId();
  const trigger = useRef(null);
  const tooltip = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0 });
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const anchor = trigger.current.getBoundingClientRect();
      const card = tooltip.current.getBoundingClientRect();
      const left = Math.max(12, Math.min(anchor.left, window.innerWidth - card.width - 12));
      const top = anchor.top >= card.height + 20 ? anchor.top - card.height - 8
        : Math.min(anchor.bottom + 8, Math.max(12, window.innerHeight - card.height - 12));
      setPosition({ left, top });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);
  return <span ref={trigger} className="dd-gloss-term" tabIndex="0" aria-describedby={open ? id : undefined}
    onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
    onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} onClick={(event) => { event.stopPropagation(); setOpen(true); }}
    onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false); }}>
    {label}
    {open && createPortal(<span ref={tooltip} id={id} className="dd-gloss-tooltip" role="tooltip" style={position}>
      <strong>{entry.term || label}</strong><br />
      <RichText text={entry.text || ''} enableHover={false} />
    </span>, trigger.current.closest('.dd-report'))}
  </span>;
};

const wrapHoverText = (text, key, hover) => {
  const raw = textValue(text);
  if (!raw || !hover?.regex) return [raw];
  const out = [];
  let last = 0;
  let count = 0;
  hover.regex.lastIndex = 0;
  let match;
  while ((match = hover.regex.exec(raw)) !== null) {
    const label = match[0];
    const alias = matchingAlias(label, hover);
    if (!alias) continue;
    if (match.index > last) out.push(raw.slice(last, match.index));
    const tooltipId = `${key}-hover-${count}`;
    out.push(
      <JargonTerm key={tooltipId} label={label} entry={alias.entry} />
    );
    count += 1;
    last = match.index + label.length;
  }
  if (last < raw.length) out.push(raw.slice(last));
  return out.length ? out : [raw];
};

const citationFor = (part, sources) => {
  const docId = part.split(',', 1)[0].trim();
  const source = sources?.byId?.get(docId);
  return source ? { ...source, label: source.label || docId } : null;
};

const citationsFor = (token, sources) => {
  const found = token.slice(1, -1).split(';').map(part => citationFor(part, sources));
  return found.every(Boolean) ? found : null;
};

const renderRich = (text, prefix, hover, sources) => {
  const normalized = textValue(text).replace(/ -- /g, ' — ');
  const token = /(\[[^\]\n]+\]|\*\*[^*]+\*\*|\*[^*]+\*|\n)/g;
  const out = [];
  let last = 0;
  let i = 0;
  let match;
  while ((match = token.exec(normalized)) !== null) {
    if (match.index > last) out.push(...wrapHoverText(normalized.slice(last, match.index), `${prefix}-${i}`, hover));
    const value = match[0];
    if (value === '\n') {
      out.push(<br key={`${prefix}-br-${i}`} />);
    } else if (value.startsWith('[')) {
      const citations = citationsFor(value, sources);
      if (citations) {
        for (let ci = 0; ci < citations.length; ci += 1) {
          const citation = citations[ci];
          out.push(<a key={`${prefix}-cite-${i}-${ci}`} className="dd-cite" title={citation.label}
            href={`#report-source-${citation.index + 1}`}>[{citation.index + 1}]</a>);
        }
      } else out.push(...wrapHoverText(value, `${prefix}-literal-${i}`, hover));
    } else if (value.startsWith('**')) {
      out.push(<strong key={`${prefix}-b-${i}`}>{renderRich(value.slice(2, -2), `${prefix}-b-${i}`, hover, sources)}</strong>);
    } else {
      out.push(<em key={`${prefix}-i-${i}`}>{renderRich(value.slice(1, -1), `${prefix}-i-${i}`, hover, sources)}</em>);
    }
    i += 1;
    last = match.index + value.length;
  }
  if (last < normalized.length) out.push(...wrapHoverText(normalized.slice(last), `${prefix}-${i}`, hover));
  return out;
};

export const RichText = ({ text, prefix = 'rt', enableHover = true }) => {
  const hover = useContext(HoverContext);
  const sources = useContext(SourcesContext);
  return <>{renderRich(text, prefix, enableHover ? hover : null, sources)}</>;
};

const cleanParagraphs = (paragraphs) => list(paragraphs).map((paragraph) => (
  textValue(paragraph).split(/\r?\n/).filter((line) => !/^\s*={3,}\s*[^=\r\n]+?\s*={3,}\s*$/.test(line)).join('\n').trim()
)).filter(Boolean);

const groupBlocks = (blocks) => {
  const grouped = [];
  let card = null;
  list(blocks).forEach((block, index) => {
    if (block.type === 'prose' && !cleanParagraphs(block.paragraphs).length) return;
    if (['prose', 'list', 'table'].includes(block.type)) {
      if (!card) {
        card = { type: 'narrative', children: [] };
        grouped.push(card);
      }
      card.children.push({ block, index });
    } else {
      card = null;
      grouped.push(block);
    }
  });
  return grouped;
};

const quizLabel = (text) => {
  const label = textValue(text).trim();
  const emphasis = /^(\*\*|__|\*|_)([\s\S]*)\1$/.exec(label);
  if (emphasis) return `${emphasis[1]}${quizLabel(emphasis[2])}${emphasis[1]}`;
  return label.replace(/\s*\**\((?:in)?correct\)\**\s*$/i, '');
};

const RichParagraphs = ({ paragraphs, lede, prefix }) => (
  <>{cleanParagraphs(paragraphs).map((paragraph, index) => (
    <p key={`${prefix}-${index}`} className={lede && index === 0 ? 'dd-lede' : undefined}>
      <RichText text={paragraph} prefix={`${prefix}-${index}`} />
    </p>
  ))}</>
);

const Link = ({ url, children }) => {
  const href = safeHref(url);
  return href ? <a href={href} target="_blank" rel="noopener noreferrer">{children || hostOf(href)}</a> : <>{children}</>;
};

const VerdictPill = ({ mark, children }) => <span className={`dd-mark ${mark || 'mixed'}`}>{children}</span>;

const workspaceHasTicker = (workspace, ticker) => {
  const wanted = textValue(ticker).toUpperCase();
  return workspace.companies.some((company) => company.ticker === wanted)
    || workspace.complimentary.some((report) => report.ticker === wanted)
    || workspace.free_company?.ticker?.toUpperCase() === wanted
    || list(workspace.unlocks).some((unlock) => unlock.ticker === wanted)
    || list(workspace.included).includes(wanted)
    || workspace.firstlook?.assigned_ticker?.toUpperCase() === wanted
    || list(workspace.firstlook?.included_tickers).includes(wanted);
};

const RivalAction = ({ rival, workspace, onAddCompany, onOpenCompany, publicMode }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [offeringDeepDive, setOfferingDeepDive] = useState(false);
  const rivalTicker = textValue(rival.ticker).toUpperCase();
  if (publicMode) return null;
  if (!rivalTicker) return <span className="dd-badge none">Not a public company</span>;
  if (workspace.loading) return <span className="dd-badge none">Checking access&hellip;</span>;
  if (workspace.error) return <span className="dd-error dd-rival-error" role="alert">{workspace.error}</span>;
  const trackedCompany = workspace.companies.find((company) => company.ticker === rivalTicker);
  const directDeepDive = workspace.free_company?.ticker?.toUpperCase() === rivalTicker
    || workspace.complimentary.some((report) => report.ticker === rivalTicker)
    || list(workspace.included).includes(rivalTicker)
    || list(workspace.unlocks).some((unlock) => unlock.ticker === rivalTicker)
    || (workspace.plan?.id === 'tier_0' && workspace.firstlook?.assigned_ticker?.toUpperCase() === rivalTicker);
  const deepDiveOwned = directDeepDive || Boolean(trackedCompany
    && (workspace.full_report_limit === null
      || (workspace.full_report_limit > 0 && trackedCompany.full_report)));
  const firstLookOwned = workspaceHasTicker(workspace, rivalTicker);
  const exemptFirstLook = list(workspace.firstlook?.exempt_tickers).includes(rivalTicker);
  const deepDiveCount = workspace.full_report_count ?? workspace.companies.filter((company) => company.full_report && !company.upgrade_carryover).length;
  const deepDiveAvailable = workspace.full_report_limit === null || workspace.full_report_limit > deepDiveCount;
  const trackingAvailable = workspace.limit == null || (workspace.count ?? workspace.companies.length) < workspace.limit;
  const firstLookAvailable = workspace.firstlook?.all || workspace.plan?.id === 'tier_3'
    ? exemptFirstLook || trackingAvailable
    : workspace.firstlook
      ? exemptFirstLook || list(workspace.firstlook.picks).includes(rivalTicker)
        || workspace.firstlook.remaining === null || workspace.firstlook.remaining > 0
      : trackingAvailable;
  const openRival = (event, product) => {
    if (!onOpenCompany) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    onOpenCompany(rivalTicker, product);
  };
  const handleAdd = async (product) => {
    setBusy(true);
    setError('');
    try {
      await onAddCompany(rivalTicker, rival.name || rivalTicker, product);
      setOfferingDeepDive(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return <span className="dd-rival-actions">
    {['firstlook', 'deepdive'].map((product) => {
      const owned = product === 'firstlook' ? firstLookOwned : deepDiveOwned;
      const available = product === 'firstlook' ? firstLookAvailable : deepDiveAvailable;
      const label = product === 'firstlook' ? 'First Looks' : 'Deep Dives';
      if (owned) return <a key={product} className="dd-badge full" href={`/company/${encodeURIComponent(rivalTicker)}?product=${product}`} onClick={(event) => openRival(event, product)}>Open {PRODUCT_LABELS[product]} &rarr;</a>;
      if (!available) return <a key={product} className="dd-badge link" href="/plans" title={`Upgrade your plan for more ${label}`}>Add to {label} <span aria-hidden="true">&uarr;</span> Upgrade</a>;
      return <button key={product} type="button" className="dd-badge link" disabled={busy} onClick={() => product === 'deepdive' ? setOfferingDeepDive(true) : handleAdd(product)}>{busy && !offeringDeepDive ? 'Adding\u2026' : `Add to ${label}`}</button>;
    })}
    {error && !offeringDeepDive && <span className="dd-error dd-rival-error" role="alert">{error}</span>}
    {offeringDeepDive && createPortal(<FullReportOffer ticker={rivalTicker} fullReport={{ limit: workspace.full_report_limit, count: deepDiveCount, error }} saving={busy} onUse={() => handleAdd('deepdive')} onClose={() => { setOfferingDeepDive(false); setError(''); }} />, document.body)}
  </span>;
};

const QuizBlock = ({ block, prefix }) => {
  const [choice, setChoice] = useState(null);
  const options = list(block.options).map((option) => ({ ...option, text: quizLabel(option.text) }));
  const interactive = list(block.options).some((option) => Object.prototype.hasOwnProperty.call(option, 'correct')) || Boolean(block.feedback);
  if (!interactive) {
    return <div className="dd-card dd-quiz preview"><h3><RichText text={block.question} prefix={`${prefix}-q`} /></h3><ul>{options.map((option, i) => <li key={i}><RichText text={option.text} prefix={`${prefix}-opt-${i}`} /></li>)}</ul><small className="dd-preview-label">Preview only</small></div>;
  }
  const selected = choice == null ? null : block.options?.[choice];
  return <div className="dd-card dd-quiz"><h3><RichText text={block.question} prefix={`${prefix}-q`} /></h3><div className="dd-learning-options">{options.map((option, i) => <button key={i} type="button" className={choice === i ? 'selected' : ''} onClick={() => setChoice(i)}><RichText text={option.text} prefix={`${prefix}-opt-${i}`} /></button>)}</div>{selected && <div className={`dd-answer ${selected.correct ? 'correct' : 'incorrect'}`} role="status"><strong>{selected.correct ? 'Correct.' : 'Not quite.'}</strong>{block.feedback && <p><RichText text={block.feedback} prefix={`${prefix}-feedback`} /></p>}</div>}</div>;
};

const RetrievalBlock = ({ block, prefix, onSelectTab }) => {
  const [choice, setChoice] = useState(null);
  const [becauseChoice, setBecauseChoice] = useState(null);
  const [checked, setChecked] = useState(false);
  const interactive = block.answer != null && block.because_answer != null && list(block.because).length > 0;
  if (!interactive) return <div className="dd-card dd-retrieval preview">{block.kind && <span className="dd-badge full">{block.kind}</span>}<h3><RichText text={block.stem} prefix={`${prefix}-stem`} /></h3><ul>{list(block.options).map((option, i) => <li key={i}><RichText text={option.text} prefix={`${prefix}-opt-${i}`} /></li>)}</ul><small className="dd-preview-label">Preview only</small></div>;
  const answer = block.options?.[block.answer];
  const chosen = choice == null ? null : block.options?.[choice];
  const correct = checked && choice === block.answer && becauseChoice === block.because_answer;
  return <div className="dd-card dd-retrieval">{block.kind && <span className="dd-badge full">{block.kind}</span>}<h3><RichText text={block.stem} prefix={`${prefix}-stem`} /></h3><p className="dd-note">Choose an answer, then choose the Because… reason.</p><div className="dd-learning-options">{list(block.options).map((option, i) => <button key={i} type="button" className={choice === i ? 'selected' : ''} onClick={() => { setChoice(i); setChecked(false); }}><RichText text={option.text} prefix={`${prefix}-opt-${i}`} /></button>)}</div>{choice != null && <><h4>Because…</h4><div className="dd-learning-options because">{list(block.because).map((reason, i) => <button key={i} type="button" className={becauseChoice === i ? 'selected' : ''} onClick={() => { setBecauseChoice(i); setChecked(false); }}><RichText text={reason} prefix={`${prefix}-because-${i}`} /></button>)}</div></>}<button className="secondary-button dd-check-button" type="button" disabled={choice == null || becauseChoice == null} onClick={() => setChecked(true)}>Check my answer</button>{checked && <div className={`dd-answer ${correct ? 'correct' : 'incorrect'}`} role="status"><strong>{correct ? 'Correct.' : 'Not quite.'}</strong><p>Correct answer: <RichText text={answer?.text} prefix={`${prefix}-correct`} /></p>{chosen?.misconception && choice !== block.answer && <p>Misconception: <RichText text={chosen.misconception} prefix={`${prefix}-misconception`} /></p>}{block.feedback && <p><RichText text={block.feedback} prefix={`${prefix}-feedback`} /></p>}{block.lesson && <button type="button" className="text-action dd-lesson-link" onClick={() => onSelectTab?.(`lesson_${block.lesson}`)}>Review lesson {block.lesson}</button>}</div>}</div>;
};

const ChainBlock = ({ block, prefix }) => <div className="dd-card dd-chain"><ol>{list(block.links).map((link, i) => <li key={i}><span><RichText text={link.from} prefix={`${prefix}-from-${i}`} /></span><b>{link.verb}</b><span><RichText text={link.to} prefix={`${prefix}-to-${i}`} /></span></li>)}</ol></div>;

const AnswerCard = ({ item, prefix }) => <article className="dd-card">
  <div className="mk-tests">Tests: {item.tests || 'Not on Start Here'}</div>
  <h3>{item.question}</h3>
  {item.asked && <div className="dd-note">Asked {item.asked}</div>}
  {Array.isArray(item.reader_sections) ? <div className="dd-answer-blocks">{item.reader_sections.map((section) => <section key={section.key} className={`dd-card reader-section${section.key === 'short' ? ' mk-short' : ''}`}>
    <p className="mk-label">{section.title}</p>
    {list(section.blocks).map((block, i) => <Block key={i} block={block} index={`${prefix}-${section.key}-${i}`} inCard />)}
  </section>)}</div> : Array.isArray(item.answer_blocks) ? <div className="dd-answer-blocks">{item.answer_blocks.map((block, i) => <Block key={i} block={block} index={`${prefix}-answer-${i}`} />)}</div> : <RichParagraphs paragraphs={item.answer} prefix={`${prefix}-answer`} />}
  {list(item.sources).length > 0 && <div className="dd-src">Sources: {item.sources.map((source, i) => <React.Fragment key={i}>{i > 0 && ' · '}<Link url={source.url}>{source.label || hostOf(source.url)}</Link></React.Fragment>)}</div>}
  {list(item.older).map((older, i) => <details className="mk-older" key={i}><summary>Earlier answer, {older.asked}: {older.question}</summary><AnswerCard item={older} prefix={`${prefix}-older-${i}`} /></details>)}
</article>;

const Block = ({ block, index, workspace, onAddCompany, onOpenCompany, publicMode, onSelectTab, inCard = false }) => {
  const type = block?.type;
  const prefix = `block-${index}`;
  if (type === 'narrative') return <div className="dd-card dd-prose">{block.children.map((child) => <Block key={child.index} block={child.block} index={`${index}-${child.index}`} inCard />)}</div>;
  if (type === 'locked') {
    return <div className="dd-upgrade-note"><span className="dd-lock" aria-hidden="true">🔒</span><h3>Keep reading with an upgraded plan</h3><p>This preview stops here. Upgrade to unlock the full section.</p></div>;
  }
  if (type === 'heading') return <h2 className="dd-sec"><RichText text={block.text} prefix={prefix} /></h2>;
  if (type === 'note') return <div className="dd-note"><RichText text={block.text} prefix={prefix} /></div>;
  if (type === 'prose') {
    const paragraphs = cleanParagraphs(block.paragraphs);
    if (inCard) return <RichParagraphs paragraphs={paragraphs} lede={block.lede} prefix={prefix} />;
    return paragraphs.length ? <div className="dd-card dd-prose"><RichParagraphs paragraphs={paragraphs} lede={block.lede} prefix={prefix} /></div> : null;
  }
  if (type === 'list') {
    const Tag = block.ordered ? 'ol' : 'ul';
    const items = <Tag>{list(block.items).map((item, i) => <li key={i}><RichText text={item} prefix={`${prefix}-${i}`} /></li>)}</Tag>;
    return inCard ? items : <div className="dd-card">{items}</div>;
  }
  if (type === 'cards') return <div className="dd-cards">{list(block.cards).map((card, i) => <div className={`dd-stat ${card.tone ? `tone-${card.tone}` : ''}`} key={i}><div className="lbl">{card.label}</div><div className="num"><RichText text={card.value} prefix={`${prefix}-v-${i}`} /></div>{card.caption && <div className="cap"><RichText text={card.caption} prefix={`${prefix}-c-${i}`} /></div>}</div>)}</div>;
  if (type === 'table') return <div className="dd-table-wrap"><table className="dd-table"><thead><tr>{list(block.columns).map((col, i) => <th key={i} className={col.align === 'right' ? 'r' : undefined}><RichText text={col.label} prefix={`${prefix}-head-${i}`} /></th>)}</tr></thead><tbody>{list(block.rows).map((row, ri) => <tr key={ri} className={`${row.bold ? 'bold-row' : ''} ${row.note ? 'note-row' : ''}`}>{list(row.cells).map((cell, ci) => <td key={ci} className={block.columns?.[ci]?.align === 'right' ? 'r' : undefined}>{typeof cell === 'object' && cell !== null && 'text' in cell ? <VerdictPill mark={cell.mark}><RichText text={cell.text} prefix={`${prefix}-${ri}-${ci}`} /></VerdictPill> : <RichText text={cell} prefix={`${prefix}-${ri}-${ci}`} />}{ci === 0 && row.note && <small className="dd-row-note"><RichText text={row.note} prefix={`${prefix}-note-${ri}`} /></small>}</td>)}</tr>)}</tbody></table></div>;
  if (type === 'callout') return <div className={`dd-callout ${block.tone || 'neutral'}`}>{block.verdict && <span className={`dd-verdict ${block.tone || 'neutral'}`}>{block.verdict}</span>}<RichParagraphs paragraphs={block.paragraphs} prefix={prefix} /></div>;
  if (type === 'ledger') return <div className="dd-ledger"><div className="dd-col bull"><h4>Evidence for the bull side</h4><ul>{list(block.bull).map((item, i) => <li key={i}><RichText text={item} prefix={`${prefix}-bull-${i}`} /></li>)}</ul></div><div className="dd-col bear"><h4>Evidence for the bear side</h4><ul>{list(block.bear).map((item, i) => <li key={i}><RichText text={item} prefix={`${prefix}-bear-${i}`} /></li>)}</ul></div></div>;
  if (type === 'scoreboard') return <><div className="dd-scoreboard">{list(block.cards).map((card, i) => <div className={`dd-sb ${card.tone ? `tone-${card.tone}` : ''}`} key={i}><h4>{card.title}</h4><div className="big"><RichText text={card.big} prefix={`${prefix}-big-${i}`} /></div><div className="sub"><RichText text={card.sub} prefix={`${prefix}-sub-${i}`} /></div></div>)}</div>{block.note && <div className="dd-note"><RichText text={block.note} prefix={`${prefix}-note`} /></div>}</>;
  if (type === 'rivals') return <div className="dd-rivals">{list(block.items).map((rival, i) => <div className="dd-card" key={i}><h3>{rival.name}{rival.ticker ? ` (${rival.ticker})` : ''} <RivalAction rival={rival} workspace={workspace} onAddCompany={onAddCompany} onOpenCompany={onOpenCompany} publicMode={publicMode} /></h3>{rival.note && !/^Search-grounded (?:profile|note) — no profile page exists for this name\.$/i.test(rival.note) && <div className="dd-note"><RichText text={rival.note} prefix={`${prefix}-note-${i}`} /></div>}<RichText text={rival.text} prefix={`${prefix}-text-${i}`} /></div>)}</div>;
  if (type === 'news') return <div className="dd-news-list">{list(block.items).map((item, i) => <article className="dd-card dd-news-item" key={i}><div className="dd-news-meta"><span>{item.date}</span><span className={`dd-badge ${item.kind === 'pr' ? 'warn' : 'full'}`}>{item.kind === 'pr' ? 'PR' : 'News'}</span><span>{item.source}</span></div><h3>{safeHref(item.url) ? <Link url={item.url}>{item.title}</Link> : item.title}</h3>{item.summary && <p><RichText text={item.summary} prefix={`${prefix}-${i}`} /></p>}</article>)}</div>;
  if (type === 'qa') {
    const cards = <div className="dd-qa-list">{list(block.items).map((item, i) => <AnswerCard key={i} item={item} prefix={`${prefix}-${i}`} />)}</div>;
    return block.folded ? <details className="mk-older"><summary>Older questions</summary>{cards}</details> : cards;
  }
  if (type === 'glossary') return <div className="dd-card dd-glossary-list">{list(block.terms).map((term, i) => <p key={i}><b>{term.term}</b> — <RichText text={term.text} prefix={`${prefix}-${i}`} /></p>)}</div>;
  if (type === 'links') return <div className="dd-card"><ul>{list(block.items).map((item, i) => <li key={i}><Link url={item.url}>{item.label || item.url}</Link>{item.detail && <> — <RichText text={item.detail} prefix={`${prefix}-${i}`} /></>}</li>)}</ul></div>;
  if (type === 'quiz') return <QuizBlock block={block} prefix={prefix} />;
  if (type === 'retrieval') return <RetrievalBlock block={block} prefix={prefix} onSelectTab={onSelectTab} />;
  if (type === 'chain') return <ChainBlock block={block} prefix={prefix} />;
  return null;
};

const TabRail = ({ tabs, active, onSelect, bottom = false }) => (
  <nav className={`dd-tabs ${bottom ? 'bottom' : ''}`} aria-label={bottom ? 'Navigate to next section' : 'Report sections'}>
    {list(tabs).map((tab) => {
      const locked = tab.level === 'locked';
      const tierClass = ['tier_1', 'tier_2', 'tier_3'].includes(tab.required_tier?.id) ? tab.required_tier.id : '';
      if (locked && tab.required_tier) return <a key={tab.id} className={`dd-tab upsell ${tierClass}`} href="/plans" title={`Upgrade to ${tab.required_tier.label} to open ${tab.label}`}>{tab.label}<small><span aria-hidden="true">🔒 </span>{tab.required_tier.label}</small></a>;
      return <button key={tab.id} type="button" className={`dd-tab ${tierClass} ${active === tab.id ? 'active' : ''} ${locked ? 'locked' : ''}`} disabled={locked} title={locked ? 'Not included in your plan' : undefined} onClick={() => onSelect(tab.id)} aria-current={active === tab.id ? 'page' : undefined}>{locked && <span aria-hidden="true">🔒 </span>}{tab.label}</button>;
    })}
  </nav>
);

const PRODUCT_ORDER = Object.keys(PRODUCT_LABELS);
const productRank = (item) => (PRODUCT_ORDER.includes(item) ? PRODUCT_ORDER.indexOf(item) : PRODUCT_ORDER.length);

const ProductSwitcher = ({ products, product, onChange, upsellProducts = [], onSelectUpsell }) => {
  if (products.length + upsellProducts.length <= 1 && !upsellProducts.length) return null;
  const ordered = [...products].sort((a, b) => productRank(a) - productRank(b));
  return <div className="dd-product-switcher" aria-label="Report product">
    {ordered.map((item) => <button key={item} type="button" className={item === product ? 'active' : ''} aria-pressed={item === product} onClick={() => onChange(item)}>{PRODUCT_LABELS[item] || item}</button>)}
    {upsellProducts.map((item) => onSelectUpsell ? <button key={item} className="upsell" type="button" onClick={() => onSelectUpsell(item)}>{PRODUCT_LABELS[item] || item}</button> : <a key={item} className="upsell" href="/plans">{PRODUCT_LABELS[item] || item}</a>)}
  </div>;
};

// Shown before allocating a Deep Dive selection to a tracked company.
const FullReportOffer = ({ ticker, fullReport, saving, onUse, onClose }) => {
  const remaining = fullReport.limit === null ? Infinity : Math.max(fullReport.limit - fullReport.count, 0);
  const dialog = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    dialog.current?.querySelector('button')?.focus();
    return () => previous?.focus();
  }, []);
  return <div className="modal-backdrop" role="presentation"><section ref={dialog} className="confirmation-modal" role="dialog" aria-modal="true" aria-labelledby="full-report-offer-title" onKeyDown={(event) => {
    if (event.key === 'Tab') {
      const controls = [...dialog.current.querySelectorAll('button:not(:disabled), a[href]')];
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && event.target === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && event.target === last) { event.preventDefault(); first?.focus(); }
    }
  }}>
    <p className="eyebrow">Deep Dive allocation</p>
    {remaining > 0 ? <>
      <h2 id="full-report-offer-title">Get the full Deep Dive for {ticker}?</h2>
      <p>{fullReport.limit === null ? 'Your plan includes unlimited Deep Dive selections.' : `This uses 1 of your ${remaining} remaining Deep Dive selections (${fullReport.count} / ${fullReport.limit} selected). This choice cannot be undone.`}</p>
      <p>First Look is included for free. Any First Look selection used for this company this month is returned to your allowance.</p>
    </> : <>
      <h2 id="full-report-offer-title">You&apos;ve used all your Deep Dive selections</h2>
      <p>All {fullReport.limit} of your Deep Dive selections are in use. Upgrade your plan to get the full Deep Dive for {ticker}.</p>
      <p>Deep Dive includes this company&apos;s First Look for free. Choosing its Deep Dive returns any First Look selection used for this company this month to your allowance.</p>
    </>}
    {fullReport.error && <div className="dd-error" role="alert">{fullReport.error}</div>}
    <div className="modal-actions">
      <button className="secondary-button" type="button" onClick={onClose} disabled={saving}>Not now</button>
      {remaining > 0 ? <button className="primary-button" type="button" onClick={onUse} disabled={saving}>{saving ? 'Saving…' : 'Confirm Deep Dive'}</button> : <a className="primary-button" href="/plans">See plans</a>}
    </div>
  </section></div>;
};

const HoldingLabels = ({ holding, workspace, ticker, user }) => {
  const labels = [];
  if (holding.includes('pick')) labels.push('Your pick');
  if (holding.includes('unlock')) {
    const unlock = list(workspace.unlocks).find((item) => item.ticker === ticker);
    labels.push(unlock?.expires_at ? `Unlocked until ${shortDate(unlock.expires_at)}` : 'Unlocked');
  }
  if (holding.includes('included')) {
    const sharedClientReport = (workspace.is_client || user?.is_client || (workspace.plan?.id || user?.plan) === 'client')
      && list(workspace.complimentary).some((report) => report.ticker.toUpperCase() === ticker.toUpperCase());
    labels.push(sharedClientReport ? 'Included for Clients' : 'Included with your plan');
  }
  if (!labels.length) return null;
  return <div className="dd-holding-labels" aria-label="Access status">{labels.map((label) => <span key={label}>{label}</span>)}</div>;
};

const shortDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
};

const reportDate = (value) => {
  if (!value) return '';
  const raw = textValue(value);
  if (!/^\d{4}-\d{2}-\d{2}(?:$|T)/.test(raw)) return raw;
  const calendarDate = raw.slice(0, 10);
  const date = new Date(`${calendarDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== calendarDate) return raw;
  return date.toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC'
  });
};

const Header = ({ envelope, page, ticker, versions, selectedVersion, onVersionChange, workspace, user }) => {
  const header = page?.header || {};
  const asOf = reportDate(header.as_of || page?.stub?.as_of || page?.generated_at);
  const subtitle = page?.subtitle;
  const pe = header.fwd_pe || header.pe || header.pe_forward || header.pe_trailing;
  const holding = list(envelope?.holding);
  return <>
    <h1 className="dd-ticker">{page?.title || page?.company || ticker}</h1>
    {subtitle && <div className="dd-subtitle">{subtitle}</div>}
    <HoldingLabels holding={holding} workspace={workspace} ticker={ticker} user={user} />
    {versions.length > 1 && <label className="dd-version-picker">Version<select value={selectedVersion || envelope?.version?.version_id || ''} onChange={(event) => onVersionChange(event.target.value)}>{versions.map((version) => <option key={version.version_id} value={version.version_id}>{niceDate(version.created_at)}{version.version_id === envelope.current_version ? ' (latest)' : ''}</option>)}</select></label>}
    <div className="dd-metaline">
      <span><b>{page?.exchange ? `${page.exchange}:` : 'Ticker:'}</b> {ticker}</span>
      {envelope?.product && <span>Product: <b>{PRODUCT_LABELS[envelope.product] || envelope.product}</b></span>}
      {header.price && <span>Price: <b>{header.price}</b></span>}
      {(header.mktcap || header.market_cap) && <span>Mkt Cap: <b>{header.mktcap || header.market_cap}</b></span>}
      {pe && <span>P/E (fwd): <b>{pe}</b></span>}
      {header.kpi_period && <span>KPIs: <b>{header.kpi_period}</b></span>}
      {asOf && <span>As of: <b>{asOf}</b></span>}
    </div>
    <hr className="dd-rule" />
  </>;
};

const LockedPanel = () => <div className="dd-locked"><span className="dd-lock" aria-hidden="true">🔒</span><h2>Not included in your plan</h2><p>This section exists, but your current plan does not include it.</p></div>;

const AccessNotice = ({ publicMode, entitled, holding, ticker, page, workspace, onAddCompany, grammarMode, product, fullReport, deepdiveEntitled, firstlookEntitled, onSelectDeepDive }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (grammarMode) return null;
  if (publicMode) return <div className="dd-callout navy"><span className="dd-verdict navy">Sign in</span><p>Sign in to add {ticker} to your workspace and see the plan available to you.</p><a className="primary-button dd-cta-link" href="/">Sign in</a></div>;
  if (product === 'deepdive' && !deepdiveEntitled && firstlookEntitled) return null;
  // Admins are entitled to every company without holding it, so there's nothing to offer.
  if (entitled || list(holding).length) return null;
  if (workspace.plan?.id === 'tier_0'
    && workspace.firstlook?.assigned_ticker?.toUpperCase() === ticker.toUpperCase()
    && ['firstlook', 'deepdive'].includes(product)) return null;
  const hasRoom = !workspace.loading && (product === 'firstlook' && workspace.firstlook
    ? list(workspace.firstlook.exempt_tickers).includes(ticker)
      || workspace.firstlook.all || workspace.firstlook.remaining === null || workspace.firstlook.remaining > 0
      || list(workspace.firstlook.picks).includes(ticker)
    : workspace.limit == null || workspace.companies.length < workspace.limit);
  const add = async () => {
    setBusy(true);
    setError('');
    try {
      await onAddCompany(ticker, page?.company || page?.title || ticker);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  if (product === 'deepdive' && fullReport.available && !fullReport.selected) {
    const available = fullReport.limit === null || fullReport.limit > fullReport.count;
    return <div className="dd-callout warn"><span className="dd-verdict warn">Deep Dive</span>
      <p>Deep Dive includes First Look for free without using a First Look selection.</p>
      {available ? <button className="primary-button" type="button" onClick={onSelectDeepDive}>Choose Deep Dive</button>
        : <p>No Deep Dive selections remain. <a href="/plans">Compare plans for more report access.</a></p>}
    </div>;
  }
  return <div className="dd-callout warn"><span className="dd-verdict warn">Workspace</span><p>This company is not in your workspace.</p>{workspace.loading ? <p>Checking workspace capacity…</p> : workspace.error ? <div className="dd-error" role="alert">{workspace.error}</div> : hasRoom ? <button className="primary-button" type="button" onClick={add} disabled={busy}>{busy ? 'Adding…' : 'Add to workspace'}</button> : <p>No tracked-company slots remain. <a href="/plans">Compare plans for more report access.</a></p>}{error && <div className="dd-error" role="alert">{error}</div>}</div>;
};

const SourcesFooter = ({ sources }) => {
  if (!list(sources).length) return null;
  return <section className="dd-sources"><h2>Sources</h2><ol>{sources.map((source, index) => <li key={source.doc_id || index} id={`report-source-${index + 1}`}>{source.label || source.doc_id}</li>)}</ol></section>;
};

const ReportLayout = ({ header, children, attribution }) => <div className="app-shell">
  {header}
  <main className="dd-report"><div className="dd-wrap">{children}<ResearchDisclaimer attribution={attribution} /></div></main>
</div>;

const CompanyReport = ({ ticker, user, onBack, onLogout, onOpenProfile, onOpenCompany, publicMode = false, grammarMode = false }) => {
  const [product, setProduct] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('product') || 'deepdive';
    return grammarMode ? 'grammar' : PAUSED_REPORT_PRODUCTS.has(requested) ? 'deepdive' : requested;
  });
  const [state, setState] = useState({ loading: true, data: null, error: '' });
  const [fullReport, setFullReport] = useState({ loading: !publicMode && !grammarMode, available: false, selected: false, count: 0, limit: 0, error: '' });
  const [workspace, setWorkspace] = useState({ loading: !publicMode && !grammarMode, companies: [], complimentary: [], free_company: null, included: [], unlocks: [], requests: null, limit: null, error: '' });
  const [savingFullReport, setSavingFullReport] = useState(false);
  const [offeringFullReport, setOfferingFullReport] = useState(false);
  const [coverageRequest, setCoverageRequest] = useState({ busy: false, notice: '', error: '' });
  const [active, setActive] = useState('');
  const [selectedVersion, setSelectedVersion] = useState(() => new URLSearchParams(window.location.search).get('version') || '');
  // How the reader reached the section on screen, for engagement analytics.
  const viewEntry = useRef('open');

  const updateReportUrl = useCallback((nextProduct, version, hash) => {
    const search = new URLSearchParams();
    if (!grammarMode && nextProduct && nextProduct !== 'deepdive') search.set('product', nextProduct);
    if (version) search.set('version', version);
    window.history.replaceState(null, '', `${window.location.pathname}${search.toString() ? `?${search}` : ''}${hash || ''}`);
  }, [grammarMode]);

  const load = useCallback(async (options = {}) => {
    const query = new URLSearchParams(window.location.search);
    const requested = options.product ?? query.get('product') ?? product;
    const pausedProduct = !grammarMode && PAUSED_REPORT_PRODUCTS.has(requested);
    const requestedProduct = grammarMode ? 'grammar' : pausedProduct ? 'deepdive' : requested;
    const effectiveProduct = requestedProduct;
    const requestedVersion = pausedProduct ? '' : options.version ?? query.get('version') ?? '';
    if (!grammarMode && PAUSED_REPORT_PRODUCTS.has(query.get('product'))) {
      updateReportUrl(requestedProduct, requestedVersion, window.location.hash);
    }
    try {
      const data = grammarMode
        ? await reportApi.grammar(requestedVersion || undefined)
        : publicMode
          ? await reportApi.publicStub(ticker, effectiveProduct)
          : await reportApi.company(ticker, { version: requestedVersion || undefined, product: effectiveProduct });
      setState({ loading: false, data, error: '' });
      setProduct(data.product || effectiveProduct || 'deepdive');
      const firstOpen = list(data.page?.tabs).find((tab) => tab.level !== 'locked') || data.page?.tabs?.[0];
      setActive((current) => list(data.page?.tabs).some((tab) => tab.id === current) ? current : firstOpen?.id || '');
      setSelectedVersion(data.version?.version_id || '');
    } catch (err) {
      setState((current) => ({ ...current, loading: false, error: err.message }));
    }
  }, [grammarMode, product, publicMode, ticker, updateReportUrl]);

  useEffect(() => {
    setState({ loading: true, data: null, error: '' });
    load();
  }, [load]);

  // A missing report is being generated: check back until it is archived.
  const building = state.data?.status === 'building';
  useEffect(() => {
    if (!building) return undefined;
    const timer = setInterval(() => load({ product, version: '' }), 60000);
    return () => clearInterval(timer);
  }, [building, load, product]);

  const loadWorkspace = useCallback(async () => {
    if (publicMode || grammarMode) return;
    setFullReport((current) => ({ ...current, loading: true, error: '' }));
    setWorkspace((current) => ({ ...current, loading: true, error: '' }));
    try {
      const result = await researchApi.companies();
      const companies = result.companies || [];
      const complimentary = result.complimentary || [];
      const company = companies.find((item) => item.ticker === ticker);
      const sharedClientReport = (result.is_client || user?.is_client || (result.plan?.id || user?.plan) === 'client')
        && complimentary.some((report) => report.ticker.toUpperCase() === ticker.toUpperCase());
      const assignedReport = (result.plan?.id || user?.plan) === 'tier_0'
        && result.firstlook?.assigned_ticker?.toUpperCase() === ticker.toUpperCase();
      const includedReport = (result.firstlook?.included_tickers || []).includes(ticker)
        || result.free_company?.ticker === ticker;
      const available = Boolean(company) || (result.available_companies || []).some((item) => item.ticker === ticker);
      setFullReport({ loading: false, available, selected: Boolean(company?.full_report || (company && result.full_report_limit == null) || sharedClientReport || assignedReport || includedReport), count: result.full_report_count ?? companies.filter((item) => item.full_report && !item.upgrade_carryover).length, limit: result.full_report_limit, error: '' });
      setWorkspace({ loading: false, plan: result.plan || { id: user?.plan }, is_client: result.is_client, firstlook: result.firstlook, companies, complimentary, free_company: result.free_company, full_report_limit: result.full_report_limit, full_report_count: result.full_report_count, included: result.included || [], unlocks: result.unlocks || [], requests: result.requests || null, limit: result.limit ?? null, count: result.count, error: '' });
    } catch (err) {
      setFullReport((current) => ({ ...current, loading: false, error: err.message }));
      setWorkspace((current) => ({ ...current, loading: false, error: err.message }));
    }
  }, [grammarMode, publicMode, ticker, user?.plan, user?.is_client]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const addCompany = async (companyTicker, companyName) => {
    const company = product === 'firstlook' && !workspace.firstlook?.all
      ? await researchApi.addFirstLookPick(companyTicker, companyName)
      : await researchApi.addCompany(companyTicker, companyName);
    setWorkspace((current) => ({ ...current, companies: [...current.companies, company] }));
    await loadWorkspace();
    await load({ product, version: selectedVersion });
    return company;
  };

  const addRival = async (companyTicker, companyName, rivalProduct = 'firstlook') => {
    const planId = workspace.plan?.id || user?.plan;
    const result = rivalProduct === 'deepdive'
      ? await researchApi.setFullReport(companyTicker, true)
      : planId === 'tier_3' || workspace.firstlook?.all
        ? await researchApi.addCompany(companyTicker, companyName)
        : await researchApi.addFirstLookPick(companyTicker, companyName);
    await loadWorkspace();
    onOpenCompany?.(companyTicker, rivalProduct);
    return result;
  };

  const requestCoverage = async () => {
    setCoverageRequest({ busy: true, notice: '', error: '' });
    try {
      const item = await coverageApi.request(ticker, '');
      setCoverageRequest({ busy: false, notice: item.joined ? `Joined the existing ${ticker} research request.` : `${ticker} research request received.`, error: '' });
      if (!item.joined) {
        setWorkspace((current) => current.requests ? { ...current, requests: { ...current.requests, used: (current.requests.used || 0) + 1, remaining: Math.max((current.requests.remaining ?? 1) - 1, 0) } } : current);
      }
    } catch (err) {
      if (err.status === 409 && err.body?.covered) {
        setCoverageRequest({ busy: false, notice: '', error: `${ticker} is already covered. Refresh or open the company page.` });
      } else if (err.status === 409 && (err.body?.detail || err.message).includes('No coverage requests remain')) {
        setCoverageRequest({ busy: false, notice: '', error: 'No coverage requests remain this month.' });
        setWorkspace((current) => current.requests ? { ...current, requests: { ...current.requests, remaining: 0 } } : current);
      } else if (err.status === 403) {
        setCoverageRequest({ busy: false, notice: '', error: 'Coverage requests are not available for this plan.' });
      } else {
        setCoverageRequest({ busy: false, notice: '', error: err.message });
      }
    }
  };

  const openProduct = (nextProduct) => {
    viewEntry.current = 'product';
    setSelectedVersion('');
    updateReportUrl(nextProduct, '', '');
    setState((current) => ({ ...current, loading: true }));
    if (nextProduct === product) load({ product: nextProduct, version: '' });
    else setProduct(nextProduct);
  };

  // Confirm a selection before opening a new Deep Dive on a capped plan.
  const offersFullReport = !fullReport.loading && fullReport.available && !fullReport.selected
    && (fullReport.limit === null || fullReport.limit > 0);

  const selectProduct = (nextProduct) => {
    if (nextProduct === 'deepdive' && offersFullReport) {
      setFullReport((current) => ({ ...current, error: '' }));
      setOfferingFullReport(true);
      return;
    }
    openProduct(nextProduct);
  };

  const useFullReport = async () => {
    setSavingFullReport(true);
    setFullReport((current) => ({ ...current, error: '' }));
    try {
      const result = await researchApi.setFullReport(ticker, true);
      setFullReport((current) => ({ ...current, selected: result.full_report, count: current.count + (result.full_report ? 1 : 0) }));
      await loadWorkspace();
      setOfferingFullReport(false);
      openProduct('deepdive');
    } catch (err) {
      setFullReport((current) => ({ ...current, error: err.message }));
    } finally {
      setSavingFullReport(false);
    }
  };

  const selectVersion = (version) => {
    viewEntry.current = 'version';
    setSelectedVersion(version);
    updateReportUrl(product, version, window.location.hash || '');
    setState((current) => ({ ...current, loading: true }));
    load({ product, version });
  };

  const selectTab = (id) => {
    viewEntry.current = 'tab';
    setActive(id);
    updateReportUrl(product, selectedVersion, `#${id}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const topline = <header className="topbar">
    <Brand href="/" onClick={(event) => {
      if (onBack && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
        event.preventDefault();
        onBack();
      }
    }} />
    <HeaderNav user={user} publicMode={publicMode} onWorkspace={onBack} onLogout={onLogout} onOpenProfile={onOpenProfile} />
  </header>;
  const page = state.data?.page;
  const tabs = list(page?.tabs).filter((tab) => tab.id !== 'changes');
  const versions = list(state.data?.versions).slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const allLocked = tabs.length > 0 && tabs.every((tab) => tab.level === 'locked');
  const currentTab = tabs.find((tab) => tab.id === active) || tabs.find((tab) => tab.level !== 'locked') || tabs[0];
  const deepdiveEntitled = state.data?.deepdive_entitled
    ?? (state.data?.product === 'deepdive' && Boolean(state.data?.entitled));
  const firstlookEntitled = state.data?.firstlook_entitled
    ?? (state.data?.product === 'firstlook' && Boolean(state.data?.entitled));
  const deepdiveUpsell = !publicMode && !grammarMode && firstlookEntitled && !deepdiveEntitled;
  const availableProducts = grammarMode ? ['grammar']
    : list(state.data?.products).length ? list(state.data?.products) : [product];
  const visibleProducts = availableProducts.filter((item) => !PAUSED_REPORT_PRODUCTS.has(item));
  const products = visibleProducts.filter((item) => (
    ['firstlook', 'deepdive'].includes(item) || deepdiveEntitled
  ) && !(deepdiveUpsell && item === 'deepdive'));
  const upsellProducts = deepdiveUpsell ? ['deepdive'] : [];
  const hoverTemplate = useMemo(() => makeHover(page?.hover), [page]);
  const hover = hoverTemplate;
  useReportViewTracking({
    enabled: !publicMode && !grammarMode && !state.loading && !state.error && state.data?.status === 'ready',
    ticker: state.data?.ticker || ticker,
    product: state.data?.product || product,
    tab: currentTab,
    versionId: state.data?.version?.version_id,
    latestVersion: state.data?.current_version ? state.data?.version?.version_id === state.data.current_version : null,
    entitled: state.data?.entitled,
    entryRef: viewEntry
  });
  const sources = useMemo(() => {
    const sourceList = list(page?.sources);
    return { sources: sourceList, byId: new Map(sourceList.map((source, index) => [source.doc_id, { ...source, index }])) };
  }, [page]);

  if (state.loading) return <ReportLayout header={topline}><div className="dd-status"><p className="dd-empty">Loading {ticker}...</p></div></ReportLayout>;
  if (state.error) return <ReportLayout header={topline}><div className="dd-error" role="alert">{state.error}</div></ReportLayout>;
  if (state.data?.status === 'building') {
    const label = PRODUCT_LABELS[product] || 'report';
    const switchable = list(state.data.products).length
      ? [...new Set([...products, product].filter((item) => !PAUSED_REPORT_PRODUCTS.has(item)))] : [];
    return <ReportLayout header={topline}><ProductSwitcher products={switchable} product={product} onChange={selectProduct} upsellProducts={upsellProducts} onSelectUpsell={selectProduct} /><div className="dd-status"><h1 className="dd-ticker">{ticker}</h1><p className="dd-empty" role="status">{state.data.build?.started_by_you ? `We've started generating the ${ticker} ${label}.` : `The ${ticker} ${label} is being generated.`} This usually takes a few hours; this page will update when it&apos;s ready.</p></div></ReportLayout>;
  }
  if (state.data?.status === 'not_built') {
    const hasCoverageRequests = !publicMode && !grammarMode && !workspace.loading && (workspace.requests?.per_period || 0) > 0;
    const canRequestCoverage = hasCoverageRequests && (workspace.requests?.remaining || 0) > 0;
    return <ReportLayout header={topline}><div className="dd-status"><h1 className="dd-ticker">{ticker}</h1><p className="dd-empty">This company hasn&apos;t been researched yet.</p>{hasCoverageRequests ? <div className="dd-callout warn"><span className="dd-verdict warn">Research request</span><p>You have {workspace.requests.remaining} coverage {workspace.requests.remaining === 1 ? 'request' : 'requests'} left this {workspace.requests.period}.</p>{canRequestCoverage && <button className="primary-button" type="button" onClick={requestCoverage} disabled={coverageRequest.busy}>{coverageRequest.busy ? 'Requesting…' : `Request research for ${ticker}`}</button>}{coverageRequest.notice && <div className="dd-answer correct" role="status">{coverageRequest.notice}</div>}{coverageRequest.error && <div className="dd-error" role="alert">{coverageRequest.error}</div>}</div> : workspace.loading ? <p className="dd-empty">Checking request availability...</p> : null}</div></ReportLayout>;
  }

  return <ReportLayout header={topline} attribution={page?.attribution}>
    <ProductSwitcher products={products} product={product} onChange={selectProduct} upsellProducts={upsellProducts} onSelectUpsell={selectProduct} />
    <TabRail tabs={tabs} active={currentTab?.id} onSelect={selectTab} />
    <Header envelope={state.data} page={page} ticker={ticker} versions={versions} selectedVersion={selectedVersion} onVersionChange={selectVersion} workspace={workspace} user={user} />
    {!allLocked && <AccessNotice publicMode={publicMode} entitled={state.data?.entitled} holding={state.data?.holding} ticker={ticker} page={page} workspace={workspace} onAddCompany={addCompany} grammarMode={grammarMode} product={product} fullReport={fullReport} deepdiveEntitled={deepdiveEntitled} firstlookEntitled={firstlookEntitled} onSelectDeepDive={() => setOfferingFullReport(true)} />}
    {offeringFullReport && <FullReportOffer ticker={ticker} fullReport={fullReport} saving={savingFullReport} onUse={useFullReport} onClose={() => setOfferingFullReport(false)} />}
    <SourcesContext.Provider value={sources}><HoverContext.Provider value={hover}>{allLocked ? <section className="dd-panel"><h2 className="dd-sec">The Elevator Pitch</h2><div className="dd-card dd-prose"><RichParagraphs paragraphs={page?.stub?.pitch} lede prefix="stub" />{page?.stub?.price && <p className="dd-price-line">Price: <b>{page.stub.price}</b>{page.stub.as_of ? ` as of ${reportDate(page.stub.as_of)}` : ''}</p>}</div><AccessNotice publicMode={publicMode} entitled={state.data?.entitled} holding={state.data?.holding} ticker={ticker} page={page} workspace={workspace} onAddCompany={addCompany} grammarMode={grammarMode} product={product} fullReport={fullReport} deepdiveEntitled={deepdiveEntitled} firstlookEntitled={firstlookEntitled} onSelectDeepDive={() => setOfferingFullReport(true)} /></section> : <section className="dd-panel" key={currentTab?.id} aria-label={currentTab?.label}>{currentTab?.level === 'locked' ? <LockedPanel /> : groupBlocks(currentTab?.blocks).map((block, i) => <Block key={i} block={block} index={i} workspace={workspace} onAddCompany={addRival} onOpenCompany={onOpenCompany} publicMode={publicMode} onSelectTab={selectTab} />)}</section>}</HoverContext.Provider></SourcesContext.Provider>
    <SourcesFooter sources={page?.sources} />
    <div className="dd-sec">Navigate to Next:</div>
    <TabRail tabs={tabs} active={currentTab?.id} onSelect={selectTab} bottom />
  </ReportLayout>;
};

export default CompanyReport;
